import { formatWeight } from "../../../utils/numberUtils";

export type RebalancingExplainerMode = "active" | "paused";

export type RebalancingExplainerPhase = "start" | "signal" | "apply" | "complete";

export interface RebalancingPositionSnapshot {
  symbol: string;
  dollars: number;
  currentPct: number;
  targetPct: number;
}

export type RebalancingTradeOrderStatus = "executing" | "blocked";

export interface RebalancingTradeOrder {
  symbol: string;
  action: string;
  status: RebalancingTradeOrderStatus;
}

export interface RebalancingExplainerSnapshot {
  nlv: number;
  positions: RebalancingPositionSnapshot[];
  maxDriftPct: number;
  ordersPlaced: boolean;
  plannedAction: string | null;
  tradeOrders: RebalancingTradeOrder[] | null;
  applyHeadline: string | null;
}

export const REBALANCING_EXPLAINER_NLV = 100_000;
export const REBALANCING_TARGET_PCT = 50;

export const REBALANCING_PHASE_ORDER: RebalancingExplainerPhase[] = [
  "start",
  "signal",
  "apply",
  "complete",
];

export const REBALANCING_PHASE_LABELS: Record<RebalancingExplainerPhase, string> = {
  start: "Strategy drifted",
  signal: "Rebalance signal",
  apply: "Setting applies",
  complete: "Outcome",
};

const DRIFTED: RebalancingPositionSnapshot[] = [
  { symbol: "Fund A", dollars: 60_000, currentPct: 60, targetPct: REBALANCING_TARGET_PCT },
  { symbol: "Fund B", dollars: 40_000, currentPct: 40, targetPct: REBALANCING_TARGET_PCT },
];

const BALANCED: RebalancingPositionSnapshot[] = [
  { symbol: "Fund A", dollars: 50_000, currentPct: REBALANCING_TARGET_PCT, targetPct: REBALANCING_TARGET_PCT },
  { symbol: "Fund B", dollars: 50_000, currentPct: REBALANCING_TARGET_PCT, targetPct: REBALANCING_TARGET_PCT },
];

function buildSnapshot(
  positions: RebalancingPositionSnapshot[],
  ordersPlaced: boolean,
  plannedAction: string | null,
  tradeOrders: RebalancingTradeOrder[] | null = null,
  applyHeadline: string | null = null,
): RebalancingExplainerSnapshot {
  const maxDriftPct = Math.max(
    ...positions.map((position) => Math.abs(position.currentPct - position.targetPct)),
  );

  return {
    nlv: REBALANCING_EXPLAINER_NLV,
    positions,
    maxDriftPct,
    ordersPlaced,
    plannedAction,
    tradeOrders,
    applyHeadline,
  };
}

const PLANNED_TRADE_ORDERS: RebalancingTradeOrder[] = [
  { symbol: "Fund A", action: "Sell $10,000", status: "executing" },
  { symbol: "Fund B", action: "Buy $10,000", status: "executing" },
];

const BLOCKED_TRADE_ORDERS: RebalancingTradeOrder[] = [
  { symbol: "Fund A", action: "Sell $10,000", status: "blocked" },
  { symbol: "Fund B", action: "Buy $10,000", status: "blocked" },
];

export function getRebalancingExplainerSnapshot(
  mode: RebalancingExplainerMode,
  phase: RebalancingExplainerPhase,
): RebalancingExplainerSnapshot {
  if (phase === "start") {
    return buildSnapshot(DRIFTED, false, null);
  }

  if (phase === "signal") {
    return buildSnapshot(
      DRIFTED,
      false,
      "Sell Fund A, buy Fund B to move back toward 50 / 50.",
    );
  }

  if (phase === "apply") {
    if (mode === "active") {
      return buildSnapshot(
        DRIFTED,
        true,
        "Trading active — automated orders are being placed for every fund in the rebalance.",
        PLANNED_TRADE_ORDERS.map((order) => ({ ...order, status: "executing" })),
        "Placing rebalance orders...",
      );
    }

    return buildSnapshot(
      DRIFTED,
      false,
      "Your rebalancing pause setting applies to the entire account. Every planned automated trade is blocked — Fund A and Fund B both stay unchanged.",
      BLOCKED_TRADE_ORDERS,
      "All rebalancing paused",
    );
  }

  if (mode === "active") {
    return buildSnapshot(BALANCED, true, "Target restored: 50 / 50.", null, null);
  }

  return buildSnapshot(
    DRIFTED,
    false,
    "Drift remains because automation is paused.",
    null,
    null,
  );
}

export function formatRebalancingSplitLabel(positions: RebalancingPositionSnapshot[]): string {
  return positions
    .map((position) => `${position.symbol} ${formatWeight(position.currentPct, 0)}%`)
    .join(" / ");
}
